import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const [hoverLeft, setHoverLeft] = useState(false);
  const [hoverRight, setHoverRight] = useState(false);
  const JUMP = 5;

  if (totalPages <= 1) return null;

  const createPages = () => {
    let pages = [];

    // Case 1: total pages <= 7 -> show all
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    // Case 2: total pages > 7 -> show truncated range
    const start = Math.max(2, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);

    pages.push(1);

    // Left ellipsis
    if (start > 2) {
      pages.push("left-ellipsis");
    }

    // Middle visible pages
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Right ellipsis
    if (end < totalPages - 1) {
      pages.push("right-ellipsis");
    }

    pages.push(totalPages);
    return pages;
  };

  const pages = createPages();

  const handleClick = (page) => {
    if (typeof page === "number" && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handleJump = (direction) => {
    if (direction === "left") {
      onPageChange(Math.max(1, currentPage - JUMP));
    } else {
      onPageChange(Math.min(totalPages, currentPage + JUMP));
    }
  };

  return (
    <>
      <div className="pagination">
        {/* Prev */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft size={18} />
        </button>

        {pages.map((page, index) => {
          if (page === "left-ellipsis") {
            return (
              <span
                key={`left-${index}`}
                className="ellipsis"
                onMouseEnter={() => setHoverLeft(true)}
                onMouseLeave={() => setHoverLeft(false)}
                onClick={() => handleJump("left")}
              >
                {hoverLeft ? "<<" : "..."}
              </span>
            );
          }

          if (page === "right-ellipsis") {
            return (
              <span
                key={`right-${index}`}
                className="ellipsis"
                onMouseEnter={() => setHoverRight(true)}
                onMouseLeave={() => setHoverRight(false)}
                onClick={() => handleJump("right")}
              >
                {hoverRight ? ">>" : "..."}
              </span>
            );
          }

          return (
            <button
              key={page}
              className={page === currentPage ? "active" : ""}
              onClick={() => handleClick(page)}
            >
              {page}
            </button>
          );
        })}

        {/* Next */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <style jsx>{`
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 4px;
          font-family: 'Montserrat', sans-serif;
        }

        .pagination button {
          background: none;
          border: none;
          font-size: 13px;
          font-weight: 600;
          line-height: 1;
          cursor: pointer;
          border-radius: 6px;
          color: #212529;
          padding: 6px 10px;
          transition: all 0.2s ease;
        }

        .pagination button:hover:not(:disabled) {
          background-color: #f2f2f2;
        }

        .pagination button.active {
          background-color: #ffeb00;
          font-weight: bold;
        }

        .pagination button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .ellipsis {
          cursor: pointer;
          color: #6c757d;
          font-weight: bold;
          font-size: 14px;
          user-select: none;
          padding: 4px 8px;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .ellipsis:hover {
          background-color: #f2f2f2;
        }
      `}</style>
    </>
  );
};

export default Pagination;
